import React, { forwardRef, createContext, useContext, useCallback } from 'react';
import { useClickAway, useEnsuredForwardedRef } from 'react-use';
import classNames from 'classnames';
import Popper from '../Popper';
import styles from './Menu.module.css';

const MenuContext = createContext(null);

const Menu = forwardRef(function Menu({ anchor, open, children, className, onClose, ...rest }, forwardedRef) {
  const menuRef = useEnsuredForwardedRef(forwardedRef);
  const handleClickOutside = useCallback(
    (event) => {
      if (!anchor || anchor.contains(event.target)) {
        return;
      }

      onClose(event);
    },
    [anchor, onClose]
  );

  useClickAway(menuRef, handleClickOutside);

  return (
    <Popper anchor={anchor} open={open} onClose={onClose}>
      <div ref={menuRef} role="menu" className={classNames(styles.root, 'Everything', className)} {...rest}>
        <MenuList onClose={onClose}>{children}</MenuList>
      </div>
    </Popper>
  );
});

function MenuList({ children, className, onClose, ...rest }) {
  return (
    <MenuContext.Provider value={{ onClose }}>
      <div className={classNames(styles.list, className)} {...rest}>
        {children}
      </div>
    </MenuContext.Provider>
  );
}

Menu.List = MenuList;

function MenuItem({ children, className, onClick, ...rest }) {
  const menuContext = useContext(MenuContext);

  const handleClick = (event) => {
    if (onClick) {
      onClick(event);
    }

    if (menuContext.onClose) {
      menuContext.onClose(event);
    }
  };

  return (
    <button
      type="button"
      role="menuitem"
      className={classNames(styles.item, className)}
      onClick={handleClick}
      {...rest}
    >
      {children}
    </button>
  );
}

Menu.Item = MenuItem;

export default Menu;
